import { forwardRef, useMemo, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { Autocomplete, type AutocompleteProps } from 'ui';

export type AutocompleteOption = {
  value: string;
  label: string;
};
export type AutocompleteFieldProps<T> = AutocompleteProps<T> & {
  label: string;
  name: string;
  valueField?: keyof T;
  onSelectOption?: (value: T) => void;
};

const Field = <T extends AutocompleteOption>(
  { name, options, required = false, valueField = 'value', onSelectOption, ...rest }: AutocompleteFieldProps<T>,
  ref: React.ForwardedRef<HTMLInputElement>
) => {
  const { control } = useFormContext();
  const { field, fieldState } = useController({
    control,
    name,
    rules: {
      required: required ? 'This field is required' : undefined,
    },
  });
  const [value, setValue] = useState<T | undefined>();

  const { error } = fieldState;

  const inputValue = useMemo(() => {
    if (!field.value) {
      return undefined;
    }

    if (value) {
      return value;
    }

    const option = options.find((option) => option[valueField] === field.value);

    if (option) {
      return option;
    }

    return field.value;
  }, [field.value, options, value, valueField]);

  return (
    <Autocomplete<T>
      options={options}
      value={inputValue}
      required={required}
      onChange={(_event, newValue) => {
        if (!newValue) {
          setValue(undefined);
          return field.onChange(undefined);
        }
        setValue(newValue);
        field.onChange(newValue[valueField]);
        onSelectOption?.(newValue);
      }}
      ref={ref}
      name={name}
      error={error ? error.message : undefined}
      {...rest}
    />
  );
};

export const AutocompleteField = forwardRef(Field) as <T>(props: AutocompleteFieldProps<T>) => JSX.Element;
